import React, { Suspense, useEffect, useState } from 'react';
import { 
  Box,
  Tab,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { ArrowBack } from '@mui/icons-material';
import { Navigate, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import API from '../services/api';

const ProfileTab = React.lazy(() => import('./customer/ProfileTab'));
const QRPassesTab = React.lazy(() => import('./customer/QRPassesTab'));
const HistoryTab = React.lazy(() => import('./customer/HistoryTab'));

const CustomerProfilePage = () => {

  const { id } = useParams();
  
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  
  // Get tab name from URL
  const currentTab = pathname.split('/').pop() || 'profile';

  const handleChange = (event, newValue) => {
    navigate(`/customers/${id}/${newValue}`, {
      state: { from: state?.from }
    });
  };

  const handleBack = () => {
    // Get from from history (if exists)
    const from = state?.from;
    if (from && typeof from === 'string' && from.length > 0) {
      navigate(from, { replace: true }); // Return back
    } else {
      // If state not found return to list of customers
      navigate('/customers', { replace: true });
    }
  };

  useEffect(() => {
    const fetchCustomer = async () => {
        setIsLoading(true);
        API.get(`/api/customers/${id}`)
            .then(response => {
                const {name, phone, email, whatsapp, comment, qrCodes} = response.data;
                setCustomer({name, phone, email, whatsapp, comment, qrCodes});
                setIsLoading(false);
            })
            .catch(error => {
                if (error.response && error.response.data && error.response.data.msg) {
                    setError(error.response.data.msg);
                } else {
                    setError(error.message);
                }
                console.error('Error when getting customer:', error)
                setIsLoading(false);
            });
    };

    if (id) {
      fetchCustomer();
    }
  }, [id]);

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={currentTab}>
        <Box
          sx={{
          display: 'flex',
          justifyContent: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
          px: 2,
          py: 1,
        }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'space-between',
              width: '80%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
              <Button
                sx={{ ml: 4, flexShrink: 0 }}
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBack}
              >
                Back
              </Button>
              {error ? (
                <Typography variant="body2" color="error">
                  Error loading name
                </Typography>
              ) : customer?.name ? (
                  <Typography variant="body1">
                    Customer: {customer.name}
                  </Typography>
              ) : (
                <Typography variant="body1">Customer</Typography>
              )}
            </Box>
            <TabList
              onChange={handleChange}
              aria-label="Customer Profile Tabs"
              centered
              sx={{
                flex: '1 1 auto',
                maxWidth: '400px',
                minWidth: '300px',
                marginLeft: 'auto',
                marginRight: '35%',
                display: 'flex',
                justifyContent: 'center'
              }} >
              <Tab label="Profile" value="profile" />
              <Tab label="QRPasses" value="qrpasses" />
              <Tab label="History" value="history" />
            </TabList>
          </Box>
        </Box>
        
        <Suspense fallback={
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1300,
            }}
          >
            <CircularProgress />
          </Box>
        }>
            <Routes>
                <Route path="profile" element={<ProfileTab customer={customer} handleBack={handleBack} onSaveSuccess={(updatedCustomer) => {setCustomer(updatedCustomer);}} />} />
                <Route path="qrpasses" element={<QRPassesTab />} />
                <Route path="history" element={<HistoryTab />} />
                <Route path="*" element={<Navigate to={`/customers/${id}/profile`} replace />} />
            </Routes>
        </Suspense>
      </TabContext>
    </Box>
  );
};

export default CustomerProfilePage;
